import {
  Analytics,
  CustomError,
  HelmetCanonical,
  PageEnvelope,
  Pagination,
  useInitialFocus,
  VolunteerSearchLink,
} from "@skv/core";
import {
  FavouriteResult,
  favouritesReducer,
} from "@skv/core/src/features/favourites";
import { favouritesStore, useAppSettings, usePageEnvelope } from "@skv/state";
import { FAVOURITES_ENUM } from "@skv/state/src/state/favourites";
import qs from "qs";
import * as React from "react";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import { ThemeProvider } from "styled-components";
import { useUserDispatch } from "~/state";
import {
  FavouriteInlineSelectForm,
  OpportunityFavouriteResultCard,
  OrganisationFavouriteResultCard,
} from "~/ui";

const Favourites = (): JSX.Element => {
  const { updateUser } = useUserDispatch();
  const location = useLocation();

  const { applicationUrl } = useAppSettings();

  const { favourites, handleFavourite, handleUnfavourite } =
    favouritesStore.hooks.useFavourites();

  const { pageEnvelope } = usePageEnvelope<{
    favouriteResult: FavouriteResult;
  }>();

  // localised cache because we want to clear on unmount
  const cache = React.useRef<{
    [key: string]: FavouriteResult;
  }>(
    pageEnvelope.data?.favouriteResult
      ? {
          [location.search]: pageEnvelope.data.favouriteResult,
        }
      : {}
  );

  const [state, dispatch] = React.useReducer(
    favouritesReducer,
    pageEnvelope.data?.favouriteResult
      ? {
          loading: false,
          error: false,
          data: pageEnvelope.data.favouriteResult,
        }
      : {
          loading: true,
          error: false,
          data: null,
        }
  );

  const wrapperRef = React.useRef();
  const favouritesHeaderRef = useInitialFocus<HTMLHeadingElement>();

  const { loading, error, data } = state;

  React.useEffect(() => {
    if (cache.current[location.search]) {
      dispatch({
        type: "FAVOURITES_RESULT:FETCH:END",
        payload: {
          data: cache.current[location.search],
        },
      });

      return;
    }

    dispatch({
      type: "FAVOURITES_RESULT:FETCH:START",
    });

    fetch(`/api/favourites${location.search}`)
      .then<
        PageEnvelope<{
          favouriteResult: FavouriteResult;
        }>
      >((res) => {
        if (res.ok) {
          return res.json();
        }

        throw Error("Favourites fetch failed");
      })
      .then(({ data, errorCode, errorMessage, success }) => {
        if (success) {
          dispatch({
            type: "FAVOURITES_RESULT:FETCH:END",
            payload: {
              data: data.favouriteResult,
            },
          });

          cache.current[location.search] = data.favouriteResult;
          return;
        }

        dispatch({
          type: "FAVOURITES_RESULT:FETCH:ERROR",
          payload: {
            error: new CustomError(errorMessage, errorCode),
          },
        });
      })
      .catch((err) => {
        console.log("Network error");
      });
  }, [location.search, pageEnvelope.data]);

  const currentParams = qs.parse(location.search, { ignoreQueryPrefix: true });

  const currentFavourites = React.useMemo(() => {
    if (currentParams.show === "org") {
      return "organisations";
    }

    if (currentParams.show === "opp") {
      return "opportunities";
    }

    return null;
  }, [currentParams.show]);

  return (
    <>
      <Helmet>
        <title>Favourites</title>
      </Helmet>
      <HelmetCanonical url={`${applicationUrl}${location.pathname}`} />

      <div ref={wrapperRef}>
        {/* Main section */}
        <section className="col-md-9 mb-5 px-0">
          <header className="mb-4">
            <h1
              ref={favouritesHeaderRef}
              className="display-1 my-3 font-weight-bold"
              tabIndex={-1}
            >
              Favourites
            </h1>
            <p className="lead">
              A list of your favourite opportunities and organisations.
            </p>
          </header>
          {((): React.ReactNode => {
            if (loading) {
              return <p className="mt-3">Please wait. Loading...</p>;
            }

            if (data.totalCount) {
              return (
                <>
                  <div className="d-flex justify-content-between align-items-center">
                    <p className="font-weight-normal">
                      Showing {data.favourites.length} of {data.totalCount}{" "}
                      favourites
                    </p>

                    <FavouriteInlineSelectForm className="mb-1" />
                  </div>
                  <ul className="list-unstyled">
                    {data.favourites?.map((fav) => (
                      <li key={`${fav.type}-${fav.id}`} className="my-0">
                        <ThemeProvider
                          theme={(theme) => ({
                            ...theme,
                            dangerColor: theme.primaryColor,
                          })}
                        >
                          {fav.type === "opp" ? (
                            <OpportunityFavouriteResultCard
                              name={fav.name}
                              organisationName={fav.organisationName}
                              headingProps={{ className: "h1" }}
                              subHeadingProps={{ className: "h3" }}
                              summary={fav.description}
                              date={fav.date}
                              category={
                                fav.category?.url ? (
                                  <VolunteerSearchLink
                                    className="font-weight-normal text-dark"
                                    to={fav.category.url}
                                    searchQuery={{
                                      category: [fav.category.value],
                                    }}
                                    searchType="opp"
                                  >
                                    {fav.category.name}
                                  </VolunteerSearchLink>
                                ) : null
                              }
                              typesOfWork={
                                fav.typesOfWork.length ? (
                                  <>
                                    {fav.typesOfWork.map((type, idx) => (
                                      <React.Fragment key={idx}>
                                        {type.url ? (
                                          <VolunteerSearchLink
                                            className="font-weight-normal text-dark"
                                            to={type.url}
                                            searchQuery={{
                                              typeofwork: [type.value],
                                            }}
                                            searchType="opp"
                                          >
                                            {type.name}
                                          </VolunteerSearchLink>
                                        ) : (
                                          <span>{type.name} | testo</span>
                                        )}
                                        {idx < fav.typesOfWork.length - 1 && (
                                          <span>, </span>
                                        )}
                                      </React.Fragment>
                                    ))}
                                  </>
                                ) : null
                              }
                              to={{
                                pathname: fav.url,
                                state: {
                                  referrer: {
                                    ...location,
                                    state: {
                                      ...(location.state as Record<
                                        string,
                                        unknown
                                      >),
                                      children: "Favourites",
                                    },
                                  },
                                },
                              }}
                              locations={(() => {
                                if (fav.locations.length) {
                                  const locations = fav.locations.join(", ");
                                  return fav.onlineRemoteVolunteers
                                    ? `${locations} / Remote Friendly`
                                    : locations;
                                }

                                return <span>Online or Remote</span>;
                              })()}
                              logoUrl={fav.organisationLogoUrl}
                              isFavourited={!!favourites.opportunities[fav.id]}
                              onFavourite={() => {
                                handleFavourite(
                                  fav.id,
                                  FAVOURITES_ENUM.OPPORTUNITY
                                );
                                updateUser((prev) => ({
                                  ...prev,
                                  favouriteCount: prev.favouriteCount + 1,
                                }));
                              }}
                              onUnfavourite={() => {
                                handleUnfavourite(
                                  fav.id,
                                  FAVOURITES_ENUM.OPPORTUNITY
                                );
                                updateUser((prev) => ({
                                  ...prev,
                                  favouriteCount: prev.favouriteCount - 1,
                                }));
                              }}
                            />
                          ) : (
                            <OrganisationFavouriteResultCard
                              to={{
                                pathname: fav.url,
                                state: {
                                  referrer: {
                                    ...location,
                                    state: {
                                      ...(location.state as Record<
                                        string,
                                        unknown
                                      >),
                                      children: "Favourites",
                                    },
                                  },
                                },
                              }}
                              headingProps={{ className: "h1" }}
                              summary={fav.description}
                              locations={(() => {
                                if (fav.locations.length) {
                                  return fav.locations[0];
                                }

                                return undefined;
                              })()}
                              category={
                                fav.category?.url ? (
                                  <>
                                    <span className="sr-only">Category: </span>
                                    <VolunteerSearchLink
                                      className="font-weight-normal text-dark"
                                      to={fav.category.url}
                                      searchQuery={{
                                        category: [fav.category.value],
                                      }}
                                      searchType="org"
                                    >
                                      {fav.category.name}
                                    </VolunteerSearchLink>
                                  </>
                                ) : null
                              }
                              organisationName={fav.organisationName}
                              isFavourited={!!favourites.organisations[fav.id]}
                              onFavourite={() => {
                                handleFavourite(
                                  fav.id,
                                  FAVOURITES_ENUM.ORGANISATION
                                );
                                updateUser((prev) => ({
                                  ...prev,
                                  favouriteCount: prev.favouriteCount + 1,
                                }));
                              }}
                              onUnfavourite={() => {
                                handleUnfavourite(
                                  fav.id,
                                  FAVOURITES_ENUM.ORGANISATION
                                );
                                updateUser((prev) => ({
                                  ...prev,
                                  favouriteCount: prev.favouriteCount - 1,
                                }));
                              }}
                              logoUrl={fav.organisationLogoUrl}
                            />
                          )}
                        </ThemeProvider>
                      </li>
                    )) || null}
                  </ul>
                  <Pagination
                    isNsw={true}
                    currentPage={data.page}
                    totalPages={data.totalPages}
                    searchQuery={qs.parse(location.search, {
                      ignoreQueryPrefix: true,
                    })}
                    searchType="favourites"
                  />
                </>
              );
            }

            if (data.totalCount === 0 && currentFavourites !== null) {
              return (
                <>
                  <div className="border-bottom d-flex justify-content-between align-items-center">
                    <p className="font-weight-normal">
                      No results found for {currentFavourites}.
                    </p>
                    <FavouriteInlineSelectForm className="mb-1" />
                  </div>
                </>
              );
            }

            return (
              <p className="lead mb-3">
                Save your favourite opportunities and organisations using the
                hearts so you can easily find them again
              </p>
            );
          })()}
        </section>
      </div>
      <Analytics
        data={{
          subSection: "favourite",
          subSubSection: "favourite:search+results",
          subSubSubSection: "favourite:search+results:page1",
          pageTitle: "favourite:search+results:page1",
          pageType: "favourite:search+results",
        }}
      />
    </>
  );
};

export default Favourites;
